/**
 * After a redeploy the built asset hashes change. A browser tab still running a
 * previous build will request JS/CSS chunks whose hashes no longer exist on the
 * server, producing one of:
 *   - TypeError: Failed to fetch dynamically imported module: …/<Chunk>-<hash>.js
 *   - TypeError: error loading dynamically imported module: …/<chunk>-<hash>.js
 *   - Error: Unable to preload CSS for …/<chunk>-<hash>.css
 * (Sentry IDELINK-7P / 7Q / 9C / D5 and the per-bundle splits E7 / E8 / EA / ED / EE / EF.)
 *
 * Vite dispatches a `vite:preloadError` event on `window` whenever a dynamic
 * import — or the CSS it preloads — fails to load. We treat that as "this tab is
 * stale" and reload once to pull the fresh index.html + asset manifest, which
 * turns a broken error-boundary screen into a transparent recovery.
 *
 * A short-lived guard in sessionStorage prevents a reload loop when the asset is
 * genuinely unreachable (offline, CDN outage) rather than merely stale.
 */
const RELOAD_GUARD_KEY = 'idelink:chunk-reload-at';
const RELOAD_GUARD_WINDOW_MS = 10_000;

export const applyChunkReloadHandler = () => {
	if (typeof window === 'undefined') {
		return;
	}

	window.addEventListener('vite:preloadError', (event: Event) => {
		// We're handling this — stop Vite from rethrowing it as an uncaught error.
		event.preventDefault();

		let lastReloadAt = 0;
		try {
			lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
		} catch {
			// sessionStorage may be blocked (private mode) — fall through and reload.
		}

		// Reloaded very recently → the chunk is genuinely missing, not just stale.
		// Stop here to avoid an infinite reload loop.
		if (Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW_MS) {
			return;
		}

		try {
			sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
		} catch {
			// Storage unavailable — proceed with the reload anyway.
		}

		window.location.reload();
	});
};
